import { useState } from 'react';
import type { ProjectSettings } from '../../types';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (data: { name: string, settings: ProjectSettings }) => void;
}

export default function CreateProjectModal({ isOpen, onClose, onCreate }: Props) {
  const [name, setName] = useState('');
  const [settings, setSettings] = useState<ProjectSettings>({
    reset_hwid_cooldown: -1,
    ffa: false,
    silent: false,
    heartbeat: false,
    lightning: false,
    obfuscation_level: 'medium',
    auto_backup: true,
    ip_rate_limit: 100
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onCreate({ name, settings });
    setName('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50">
      <div className="fixed inset-0 bg-black/30" onClick={onClose} />
      
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full relative z-10 max-h-[90vh] overflow-y-auto">
          <h2 className="text-lg font-medium text-white mb-4">
            Create New Project
          </h2>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Info */}
            <div>
              <label className="block text-gray-300 mb-2">Project Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full bg-gray-700 text-white rounded p-2"
                required
              />
            </div>

            {/* Security Settings */}
            <div>
              <h3 className="text-white font-medium mb-2">Security Settings</h3>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={settings.heartbeat}
                    onChange={(e) => setSettings({...settings, heartbeat: e.target.checked})}
                    className="mr-2"
                  />
                  <span className="text-gray-300">Heartbeat Check</span>
                </label>
                
                <label className="flex items-center">
                  <input
                    type="number"
                    value={settings.ip_rate_limit}
                    onChange={(e) => setSettings({...settings, ip_rate_limit: parseInt(e.target.value)})}
                    className="w-24 bg-gray-700 text-white rounded p-1 mr-2"
                  />
                  <span className="text-gray-300">IP Rate Limit (per hour)</span>
                </label>
              </div>
            </div>

            {/* Script Settings */}
            <div>
              <h3 className="text-white font-medium mb-2">Script Settings</h3>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={settings.ffa}
                    onChange={(e) => setSettings({...settings, ffa: e.target.checked})}
                    className="mr-2"
                  />
                  <span className="text-gray-300">FFA Mode</span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={settings.silent}
                    onChange={(e) => setSettings({...settings, silent: e.target.checked})}
                    className="mr-2"
                  />
                  <span className="text-gray-300">Silent Mode</span>
                </label>

                <div>
                  <label className="block text-gray-300 mb-1">Obfuscation Level</label>
                  <select
                    value={settings.obfuscation_level}
                    onChange={(e) => setSettings({...settings, obfuscation_level: e.target.value as any})}
                    className="bg-gray-700 text-white rounded p-1"
                  >
                    <option value="basic">Basic</option>
                    <option value="medium">Medium</option>
                    <option value="advanced">Advanced</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Backup Settings */}
            <div>
              <h3 className="text-white font-medium mb-2">Backup Settings</h3>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={settings.auto_backup}
                  onChange={(e) => setSettings({...settings, auto_backup: e.target.checked})}
                  className="mr-2"
                />
                <span className="text-gray-300">Enable Auto-Backup</span>
              </label>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-300 hover:text-white"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
              >
                Create
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
} 